/**
 * SDN (Software Defined Networking) API Service
 *
 * Fornece funções para gerenciar zonas SDN, VNETs e subnets no Proxmox.
 */

import { apiClient } from './api'

// ========================================
// Types
// ========================================

export type SDNZoneType = 'simple' | 'vlan' | 'vxlan' | 'qinq' | 'evpn'

export interface SDNZone {
  zone: string
  type: SDNZoneType
  nodes?: string
  bridge?: string
  mtu?: number
  peers?: string
  controller?: string
  'vrf-vxlan'?: number
  'advertise-subnets'?: number
  digest?: string
}

export interface VNET {
  vnet: string
  zone: string
  type?: string
  tag?: number
  alias?: string
  vlanaware?: number
  digest?: string
}

export interface Subnet {
  subnet: string
  gateway?: string
  snat?: number
  'dhcp-range'?: string
  'dns-server'?: string
  digest?: string
}

export interface SDNStatus {
  total_zones: number
  total_vnets: number
  zones_by_type: Record<string, number>
  vnets_by_zone: Record<string, number>
  zones: SDNZone[]
  vnets: VNET[]
}

export interface SDNValidation {
  valid: boolean
  errors: string[]
  warnings: string[]
}

export interface SDNZoneTemplate {
  template_id: string
  name: string
  description: string
  zone_type: SDNZoneType
  recommended_use: string
  default_config: Record<string, any>
}

export interface SDNPendingStatus {
  has_pending_changes: boolean
  count: number
  last_applied_at: string | null
}

// ========================================
// Dedicated Zones Types
// ========================================

export interface DedicatedZoneOrganization {
  organization: {
    id: string
    name: string
    type: string
    network_isolation_level: string
  }
  dedicated_zone: {
    id: string
    zone_id: string
    name: string
    zone_type: string
    cluster_id: string
  } | null
}

export interface AssignedReseller {
  id: string
  name: string
  type: 'reseller'
  is_default: boolean
  assignment_id: string
}

export interface AvailableZone {
  id: string
  zone_id: string
  name: string
  zone_type: string
  is_assigned: boolean
  assigned_to: {
    id: string
    name: string
    type: string
  } | null
  assigned_resellers?: AssignedReseller[]
  assigned_count?: number
}

export interface EligibleOrganization {
  id: string
  name: string
  type: 'reseller'
  network_isolation_level: string
  has_this_zone: boolean
  assigned_zones_count: number
  assigned_zones: Array<{
    id: string
    zone_id: string
    name: string
    is_default: boolean
  }>
}

export interface ResellerZoneAssignmentCP {
  assignment_id: string
  zone: {
    id: string
    zone_id: string
    name: string
    zone_type: string
    cluster_id: string
    cluster_name: string
  }
  is_default: boolean
  assigned_by: string | null
  assigned_at: string
  notes: string | null
}

// ========================================
// Zone Creation Schemas
// ========================================

export interface SimpleZoneCreate {
  zone_id: string
  name: string
  description?: string
  type: 'simple'
  bridge?: string
  nodes?: string[]
  mtu?: number
}

export interface VLANZoneCreate {
  zone_id: string
  name: string
  description?: string
  type: 'vlan'
  bridge: string
  nodes?: string[]
  mtu?: number
}

export interface VXLANZoneCreate {
  zone_id: string
  name: string
  description?: string
  type: 'vxlan'
  peers: string[]
  nodes?: string[]
  mtu?: number
}

export interface EVPNZoneCreate {
  zone_id: string
  name: string
  description?: string
  type: 'evpn'
  controller: string
  vrf_vxlan: number
  advertise_subnets?: boolean
  exitnodes?: string[]
  nodes?: string[]
  mtu?: number
}

export interface QinQZoneCreate {
  zone_id: string
  name: string
  description?: string
  type: 'qinq'
  bridge: string
  tag: number
  nodes?: string[]
  mtu?: number
}

export type ZoneCreateData =
  | SimpleZoneCreate
  | VLANZoneCreate
  | VXLANZoneCreate
  | EVPNZoneCreate
  | QinQZoneCreate

export interface ZoneUpdate {
  nodes?: string[]
  mtu?: number
  bridge?: string
  peers?: string[]
  controller?: string
  vrf_vxlan?: number
  advertise_subnets?: boolean
  exitnodes?: string[]
}

// ========================================
// VNET Schemas
// ========================================

export interface VNETCreate {
  vnet_id: string
  zone: string
  tag?: number
  alias?: string
  vlanaware?: boolean
}

export interface VNETUpdate {
  zone?: string
  tag?: number
  alias?: string
  vlanaware?: boolean
}

// ========================================
// Subnet Schemas
// ========================================

export interface SubnetCreate {
  subnet: string
  gateway?: string
  snat?: boolean
  dhcp_range_start?: string
  dhcp_range_end?: string
  dns_server?: string
}

// ========================================
// SDN Zones API
// ========================================

export const sdnApi = {
  // ========================================
  // Zones
  // ========================================

  async listZones(clusterId: string): Promise<SDNZone[]> {
    const response = await apiClient.get<SDNZone[]>(`/api/v1/sdn/clusters/${clusterId}/zones`)
    return response
  },

  async getZone(clusterId: string, zoneId: string): Promise<SDNZone> {
    return await apiClient.get<SDNZone>(`/api/v1/sdn/clusters/${clusterId}/zones/${zoneId}`)
  },

  async createZone(clusterId: string, zoneData: ZoneCreateData): Promise<{ message: string }> {
    let endpoint = ''

    switch (zoneData.type) {
      case 'simple':
        endpoint = `/api/v1/sdn/clusters/${clusterId}/zones/simple`
        break
      case 'vlan':
        endpoint = `/api/v1/sdn/clusters/${clusterId}/zones/vlan`
        break
      case 'vxlan':
        endpoint = `/api/v1/sdn/clusters/${clusterId}/zones/vxlan`
        break
      case 'evpn':
        endpoint = `/api/v1/sdn/clusters/${clusterId}/zones/evpn`
        break
      case 'qinq':
        endpoint = `/api/v1/sdn/clusters/${clusterId}/zones/qinq`
        break
      default:
        throw new Error(`Unknown zone type: ${(zoneData as any).type}`)
    }

    return await apiClient.post<{ message: string }>(endpoint, zoneData)
  },

  async updateZone(
    clusterId: string,
    zoneId: string,
    zoneData: ZoneUpdate,
  ): Promise<{ message: string }> {
    return await apiClient.put<{ message: string }>(
      `/api/v1/sdn/clusters/${clusterId}/zones/${zoneId}`,
      zoneData,
    )
  },

  async deleteZone(clusterId: string, zoneId: string): Promise<void> {
    await apiClient.delete(`/api/v1/sdn/clusters/${clusterId}/zones/${zoneId}`)
  },

  async checkZoneExists(
    clusterId: string,
    zoneId: string,
  ): Promise<{ exists: boolean; exists_in_db: boolean; exists_in_proxmox: boolean }> {
    return await apiClient.get<{
      exists: boolean
      exists_in_db: boolean
      exists_in_proxmox: boolean
    }>(`/api/v1/sdn/clusters/${clusterId}/zones/${zoneId}/exists`)
  },

  // ========================================
  // VNETs
  // ========================================

  async listVNETs(clusterId: string, zoneId?: string): Promise<VNET[]> {
    const params = zoneId ? { zone_id: zoneId } : {}
    return await apiClient.get<VNET[]>(`/api/v1/sdn/clusters/${clusterId}/vnets`, { params })
  },

  async getVNET(clusterId: string, vnetId: string): Promise<VNET> {
    return await apiClient.get<VNET>(`/api/v1/sdn/clusters/${clusterId}/vnets/${vnetId}`)
  },

  async createVNET(clusterId: string, vnetData: VNETCreate): Promise<{ message: string }> {
    return await apiClient.post<{ message: string }>(
      `/api/v1/sdn/clusters/${clusterId}/vnets`,
      vnetData,
    )
  },

  async updateVNET(
    clusterId: string,
    vnetId: string,
    vnetData: VNETUpdate,
  ): Promise<{ message: string }> {
    return await apiClient.put<{ message: string }>(
      `/api/v1/sdn/clusters/${clusterId}/vnets/${vnetId}`,
      vnetData,
    )
  },

  async deleteVNET(clusterId: string, vnetId: string): Promise<void> {
    await apiClient.delete(`/api/v1/sdn/clusters/${clusterId}/vnets/${vnetId}`)
  },

  // ========================================
  // Subnets
  // ========================================

  async listSubnets(clusterId: string, vnetId: string): Promise<Subnet[]> {
    return await apiClient.get<Subnet[]>(
      `/api/v1/sdn/clusters/${clusterId}/vnets/${vnetId}/subnets`,
    )
  },

  async createSubnet(
    clusterId: string,
    vnetId: string,
    subnetData: SubnetCreate,
  ): Promise<{ message: string }> {
    return await apiClient.post<{ message: string }>(
      `/api/v1/sdn/clusters/${clusterId}/vnets/${vnetId}/subnets`,
      subnetData,
    )
  },

  async deleteSubnet(clusterId: string, vnetId: string, subnet: string): Promise<void> {
    await apiClient.delete(`/api/v1/sdn/clusters/${clusterId}/vnets/${vnetId}/subnets/${subnet}`)
  },

  // ========================================
  // Configuration Management
  // ========================================

  async applyConfiguration(clusterId: string): Promise<{ message: string }> {
    return await apiClient.post<{ message: string }>(`/api/v1/sdn/clusters/${clusterId}/apply`)
  },

  async reloadConfiguration(clusterId: string): Promise<{ message: string }> {
    return await apiClient.post<{ message: string }>(`/api/v1/sdn/clusters/${clusterId}/reload`)
  },

  // ========================================
  // Status and Helpers
  // ========================================

  async getStatus(clusterId: string): Promise<SDNStatus> {
    return await apiClient.get<SDNStatus>(`/api/v1/sdn/clusters/${clusterId}/status`)
  },

  async validateZone(zoneType: string, config: Record<string, any>): Promise<SDNValidation> {
    return await apiClient.post<SDNValidation>('/api/v1/sdn/validate-zone', {
      zone_type: zoneType,
      config,
    })
  },

  async getTemplates(): Promise<SDNZoneTemplate[]> {
    const response = await apiClient.get<{ templates: SDNZoneTemplate[] }>('/api/v1/sdn/templates')
    return response.templates
  },

  async getPendingStatus(clusterId: string): Promise<SDNPendingStatus> {
    return await apiClient.get<SDNPendingStatus>(`/api/v1/sdn/clusters/${clusterId}/pending-status`)
  },

  // ========================================
  // Infrastructure Discovery
  // ========================================

  /**
   * Lista bridges de rede disponíveis no cluster
   */
  async listBridges(clusterId: string): Promise<{
    bridges: Array<{
      name: string
      nodes: string[]
      available_on_all_nodes: boolean
    }>
    total: number
    nodes: string[]
  }> {
    return await apiClient.get(`/api/v1/sdn/clusters/${clusterId}/bridges`)
  },

  /**
   * Lista controllers SDN (para zonas EVPN)
   */
  async listControllers(clusterId: string): Promise<{
    controllers: Array<{
      controller: string
      type: string
      asn?: number
      peers?: string
    }>
    total: number
  }> {
    return await apiClient.get(`/api/v1/sdn/clusters/${clusterId}/controllers`)
  },

  /**
   * Lista nodes do cluster para seleção em zonas SDN
   */
  async listNodes(clusterId: string): Promise<{
    nodes: Array<{
      name: string
      status: string
      online: boolean
    }>
    total: number
  }> {
    return await apiClient.get(`/api/v1/sdn/clusters/${clusterId}/nodes`)
  },

  /**
   * Lista nodes do cluster com o endereço que entra no `peers` de uma zona VXLAN (#1060).
   *
   * `ip` vem do corosync (`source: "corosync"`) ou, sem ele, da única interface com
   * endereço (`source: "interface"`). Com mais de um candidato o backend NÃO escolhe:
   * `ip` é null, `reason` é `"ambiguo"` e `candidates` lista os endereços para o
   * operador informar no campo de IPs externos.
   */
  async getNodeIps(clusterId: string): Promise<{
    nodes: Array<{
      name: string
      status: string | null
      online: boolean
      ip: string | null
      source: 'corosync' | 'interface' | null
      candidates: string[]
      reason: 'offline' | 'sem_endereco' | 'ambiguo' | 'rede_ilegivel' | null
      detail: string | null
    }>
    total: number
    online_count: number
    peer_ips: string[]
    unresolved: string[]
    refusal: { code: string; message: string } | null
    suggested_vxlan_config: {
      peers: string[]
      mtu: number
      description: string
    }
  }> {
    return await apiClient.get(`/api/v1/sdn/clusters/${clusterId}/nodes/ips`)
  },

  // ========================================
  // Dedicated Zones
  // ========================================

  /**
   * Lista todas as organizações que têm zonas dedicadas
   */
  async listOrganizationsWithDedicatedZones(): Promise<{
    organizations: DedicatedZoneOrganization[]
    total: number
  }> {
    return await apiClient.get('/api/v1/sdn/dedicated-zones/organizations')
  },

  /**
   * Lista zonas disponíveis para atribuição como dedicada em um cluster
   */
  async listAvailableZonesForDedication(clusterId: string): Promise<{
    zones: AvailableZone[]
    total: number
    available: number
  }> {
    return await apiClient.get(`/api/v1/sdn/dedicated-zones/available/${clusterId}`)
  },

  /**
   * Atribui uma zona SDN dedicada a uma organização
   */
  async assignDedicatedZone(
    organizationId: string,
    zoneMetadataId: string,
  ): Promise<{
    message: string
    organization: { id: string; name: string; network_isolation_level: string }
    zone: { id: string; zone_id: string; name: string }
  }> {
    return await apiClient.post('/api/v1/sdn/dedicated-zones/assign', null, {
      params: {
        organization_id: organizationId,
        zone_metadata_id: zoneMetadataId,
      },
    })
  },

  /**
   * Remove a zona dedicada de uma organização
   */
  async removeDedicatedZone(organizationId: string): Promise<{
    message: string
    organization: { id: string; name: string; network_isolation_level: string }
  }> {
    return await apiClient.delete(`/api/v1/sdn/dedicated-zones/organizations/${organizationId}`)
  },

  /**
   * Lista todos os Resellers elegíveis para receber zonas SDN
   * @param zoneId - Se fornecido, inclui info se cada reseller já tem essa zona
   */
  async listEligibleOrganizationsForDedicatedZone(zoneId?: string): Promise<{
    organizations: EligibleOrganization[]
    total: number
    already_have_zone: number
    available: number
  }> {
    const params = zoneId ? { zone_id: zoneId } : {}
    return await apiClient.get('/api/v1/sdn/dedicated-zones/eligible-organizations', { params })
  },

  // ========================================
  // Reseller Zone Management (CP side)
  // ========================================

  /**
   * Lista zonas SDN atribuídas a um Reseller específico
   * (usado pelo Cloud Provider)
   */
  async listResellerZones(resellerId: string): Promise<{
    reseller: { id: string; name: string }
    zones: ResellerZoneAssignmentCP[]
    total: number
    default_zone: ResellerZoneAssignmentCP | null
  }> {
    return await apiClient.get(`/api/v1/sdn/resellers/${resellerId}/zones`)
  },

  /**
   * Atribui uma zona SDN a um Reseller
   * (usado pelo Cloud Provider)
   */
  async assignZoneToReseller(
    resellerId: string,
    zoneMetadataId: string,
    options?: { is_default?: boolean; notes?: string },
  ): Promise<{
    message: string
    assignment: ResellerZoneAssignmentCP
  }> {
    const params: Record<string, any> = { zone_metadata_id: zoneMetadataId }
    if (options?.is_default !== undefined) params.is_default = options.is_default
    if (options?.notes) params.notes = options.notes

    return await apiClient.post(`/api/v1/sdn/resellers/${resellerId}/zones`, null, { params })
  },

  /**
   * Remove uma zona SDN de um Reseller
   * (usado pelo Cloud Provider)
   */
  async removeZoneFromReseller(
    resellerId: string,
    assignmentId: string,
  ): Promise<{
    message: string
  }> {
    return await apiClient.delete(`/api/v1/sdn/resellers/${resellerId}/zones/${assignmentId}`)
  },

  /**
   * Cria uma zona VXLAN dedicada para um Reseller em um cluster
   * (usado pelo Cloud Provider)
   *
   * Útil quando o reseller já tem cluster atribuído mas não tem zona SDN
   */
  async createVxlanZoneForReseller(
    resellerId: string,
    clusterId: string,
  ): Promise<{
    message: string
    zone: {
      id: string
      zone_id: string
      name: string
      zone_type: string
      cluster_id: string
      cluster_name: string
    }
    assignment: {
      id: string
      reseller_id: string
      reseller_name: string
      is_default: boolean
    }
    pending_changes: number
  }> {
    return await apiClient.post(`/api/v1/sdn/resellers/${resellerId}/zones/create-vxlan`, null, {
      params: { cluster_id: clusterId },
    })
  },
}

// ========================================
// Helper Functions
// ========================================

export function getZoneTypeLabel(type: SDNZoneType): string {
  const labels: Record<SDNZoneType, string> = {
    simple: 'Simple',
    vlan: 'VLAN',
    vxlan: 'VXLAN',
    qinq: 'QinQ',
    evpn: 'EVPN',
  }
  return labels[type] || type
}

export function getZoneTypeColor(type: SDNZoneType): string {
  const colors: Record<SDNZoneType, string> = {
    simple: 'text-gray-700 bg-gray-100',
    vlan: 'text-blue-700 bg-blue-100',
    vxlan: 'text-purple-700 bg-purple-100',
    qinq: 'text-orange-700 bg-orange-100',
    evpn: 'text-green-700 bg-green-100',
  }
  return colors[type] || 'text-gray-700 bg-gray-100'
}

export function getZoneTypeDescription(type: SDNZoneType): string {
  const descriptions: Record<SDNZoneType, string> = {
    simple: 'Zona simples sem overlay',
    vlan: 'Isolamento por VLAN',
    vxlan: 'Overlay VXLAN multi-datacenter',
    qinq: 'VLAN stacking (802.1ad)',
    evpn: 'BGP EVPN com roteamento dinâmico',
  }
  return descriptions[type] || 'Tipo desconhecido'
}

export function getZoneTypeIcon(type: SDNZoneType): string {
  // Icons podem ser mapeados para componentes Lucide React
  const icons: Record<SDNZoneType, string> = {
    simple: 'Network',
    vlan: 'Layers',
    vxlan: 'Globe',
    qinq: 'Stack',
    evpn: 'Route',
  }
  return icons[type] || 'Network'
}
